import { useState } from 'react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { ChevronRight } from 'lucide-react'
import type { TeamTreeNode } from './TeamTree'
import { TeamActions } from './TeamActions'
import { MoveTeamDialog } from './MoveTeamDialog'
import { DeleteTeamDialog } from './DeleteTeamDialog'

interface TeamNodeProps {
  team: TeamTreeNode
  depth: number
  allTeams: TeamTreeNode[]
  onEdit: (team: TeamTreeNode) => void
  onCreate: (parentId?: number) => void
}

export function TeamNode({ team, depth, allTeams, onEdit, onCreate }: TeamNodeProps) {
  const hasChildren = team.children.length > 0
  const [open, setOpen] = useState(depth === 0)
  const [moveOpen, setMoveOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div
        className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50"
        style={{ paddingLeft: `${depth * 24 + 8}px` }}
      >
        {hasChildren ? (
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon" className="size-6 shrink-0">
              <ChevronRight
                className={cn(
                  'size-4 transition-transform duration-200',
                  open && 'rotate-90'
                )}
              />
            </Button>
          </CollapsibleTrigger>
        ) : (
          <div className="size-6 shrink-0" />
        )}

        <button
          type="button"
          onClick={() => onEdit(team)}
          className="cursor-pointer truncate font-medium hover:underline text-left"
        >
          {team.name}
        </button>

        <Badge variant="secondary" className="shrink-0 tabular-nums">
          {team.member_count}
        </Badge>

        {team.responsible_user_name && (
          <Badge variant="outline" className="shrink-0 truncate">
            {team.responsible_user_name}
          </Badge>
        )}

        <div className="ml-auto" onClick={(e) => e.stopPropagation()}>
          <TeamActions
            team={team}
            onEdit={() => onEdit(team)}
            onAddChild={() => onCreate(team.id)}
            onMove={() => setMoveOpen(true)}
            onDelete={() => setDeleteOpen(true)}
          />
        </div>
      </div>

      {hasChildren && (
        <CollapsibleContent>
          {team.children.map((child) => (
            <TeamNode
              key={child.id}
              team={child}
              depth={depth + 1}
              allTeams={allTeams}
              onEdit={onEdit}
              onCreate={onCreate}
            />
          ))}
        </CollapsibleContent>
      )}

      <MoveTeamDialog
        team={team}
        allTeams={allTeams}
        open={moveOpen}
        onOpenChange={setMoveOpen}
      />

      <DeleteTeamDialog
        team={team}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      />
    </Collapsible>
  )
}
